import React, { useState, useEffect } from 'react';
import AdminLayout from '../../components/admin/adminlayout';
import { apiGet, apiPost, apiPut, apiDelete } from '../../utils/api';
import { PRODUCTION_URL } from '../productionLink/productionLink';

const AdminDashboard = () => {
  const [stats, setStats] = useState({
    totalRooms: { value: 0, change: '+0%' },
    occupied: { value: 0, change: '+0%' },
    available: { value: 0, change: '+0%' },
    revenueToday: { value: 0, change: '+0%' },
    checkInsToday: { value: 0, change: '+0%' },
    checkOutsToday: { value: 0, change: '+0%' }
  });
  const [activities, setActivities] = useState([]);
  const [todayCheckIns, setTodayCheckIns] = useState([]);
  const [todayCheckOuts, setTodayCheckOuts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);

  // Color palette for stats
  const colors = {
    primary: '#2D5016',
    accent: '#6ba83e',
    lightGray: '#f8faf9',
    mediumGray: '#e8ede9',
    textDark: '#1a1a1a',
    textLight: '#6b7280',
    white: '#ffffff',
    success: '#4a7c2a',
    warning: '#f59e0b',
    danger: '#ef4444'
  };

  // Responsive breakpoints
  const isMobile = windowWidth <= 768;
  const isTablet = windowWidth > 768 && windowWidth <= 1024;
  const isDesktop = windowWidth > 1024;

  // Handle window resize
  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Fetch dashboard data
  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      setError(null);

      // Get JWT token from localStorage
      const token = localStorage.getItem('token');
      const headers = {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      };

      // Fetch statistics
      const statsResponse = await fetch(`${PRODUCTION_URL}/api/admin/stats`, { headers });
      const statsData = await statsResponse.json();

      if (statsData.success) {
        setStats(statsData.data);
      }

      // Fetch recent activities
      const activitiesResponse = await fetch(`${PRODUCTION_URL}/api/admin/activities?limit=5`, { headers });
      const activitiesData = await activitiesResponse.json();

      if (activitiesData.success) {
        setActivities(activitiesData.data);
      }

      // Fetch today's check-ins
      const checkInsResponse = await fetch(`${PRODUCTION_URL}/api/admin/checkins/today`, { headers });
      const checkInsData = await checkInsResponse.json();

      if (checkInsData.success) {
        setTodayCheckIns(checkInsData.data);
      }

      // Fetch today's check-outs
      const checkOutsResponse = await fetch(`${PRODUCTION_URL}/api/admin/checkouts/today`, { headers });
      const checkOutsData = await checkOutsResponse.json();

      if (checkOutsData.success) {
        setTodayCheckOuts(checkOutsData.data);
      }

      setLoading(false);
    } catch (err) {
      console.error('Error fetching dashboard data:', err);
      setError('Failed to load dashboard data');
      setLoading(false);
    }
  };

  // Map activity types to colors
  const getActivityColor = (type) => {
    const colorMap = {
      'booking': colors.primary,
      'check_in': colors.success,
      'payment': colors.accent,
      'check_out': colors.warning,
      'maintenance': colors.danger
    };
    return colorMap[type] || colors.primary;
  };

  // Statistics card configuration
  const statsConfig = [
    {
      key: 'totalRooms',
      label: 'Total Rooms',
      icon: '🏨',
      color: '#3b82f6',
      lightColor: 'rgba(59, 130, 246, 0.1)'
    },
    {
      key: 'occupied',
      label: 'Occupied',
      icon: '✅',
      color: '#10b981',
      lightColor: 'rgba(16, 185, 129, 0.1)'
    },
    {
      key: 'available',
      label: 'Available',
      icon: '🚪',
      color: '#f59e0b',
      lightColor: 'rgba(245, 158, 11, 0.1)'
    },
    {
      key: 'revenueToday',
      label: 'Revenue Today',
      icon: '💰',
      color: colors.accent,
      lightColor: 'rgba(107, 168, 62, 0.1)',
      isRevenue: true
    },
    {
      key: 'checkInsToday',
      label: 'Check-ins Today',
      icon: '🔑',
      color: '#8b5cf6',
      lightColor: 'rgba(139, 92, 246, 0.1)'
    },
    {
      key: 'checkOutsToday',
      label: 'Check-outs Today',
      icon: '🚶',
      color: '#ec4899',
      lightColor: 'rgba(236, 72, 153, 0.1)'
    }
  ];

  const styles = {
    pageHeader: {
      marginBottom: isMobile ? '16px' : isTablet ? '20px' : '24px',
      padding: isMobile ? '0 4px' : '0'
    },
    headerContainer: {
      display: 'flex',
      flexDirection: isMobile ? 'column' : 'row',
      justifyContent: 'space-between',
      alignItems: isMobile ? 'flex-start' : 'center',
      gap: isMobile ? '12px' : '16px'
    },
    pageTitle: {
      fontSize: isMobile ? '20px' : isTablet ? '24px' : '28px',
      fontWeight: '700',
      color: colors.textDark,
      marginBottom: '6px',
      lineHeight: '1.2'
    },
    pageSubtitle: {
      fontSize: isMobile ? '12px' : isTablet ? '13px' : '14px',
      color: colors.textLight,
      fontWeight: '400',
      lineHeight: '1.4'
    },
    statsGrid: {
      display: 'grid',
      gridTemplateColumns: isMobile
        ? '1fr'
        : isTablet
          ? 'repeat(2, 1fr)'
          : 'repeat(auto-fit, minmax(280px, 1fr))',
      gap: isMobile ? '16px' : isTablet ? '20px' : '24px',
      marginBottom: isMobile ? '24px' : isTablet ? '32px' : '40px',
      padding: isMobile ? '0 4px' : '0'
    },
    statCard: {
      background: colors.white,
      padding: isMobile ? '20px' : isTablet ? '24px' : '28px',
      borderRadius: isMobile ? '14px' : isTablet ? '16px' : '18px',
      boxShadow: '0 4px 16px rgba(45, 80, 22, 0.08)',
      transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
      border: `1px solid ${colors.mediumGray}`,
      cursor: 'pointer',
      position: 'relative',
      overflow: 'hidden'
    },
    statHeader: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginBottom: isMobile ? '14px' : isTablet ? '16px' : '20px',
      position: 'relative',
      zIndex: 1
    },
    statIcon: {
      width: isMobile ? '44px' : isTablet ? '50px' : '56px',
      height: isMobile ? '44px' : isTablet ? '50px' : '56px',
      borderRadius: isMobile ? '10px' : isTablet ? '12px' : '14px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      fontSize: isMobile ? '22px' : isTablet ? '25px' : '28px',
      boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)'
    },
    statChange: {
      padding: isMobile ? '4px 10px' : isTablet ? '5px 12px' : '6px 14px',
      borderRadius: isMobile ? '8px' : '10px',
      fontSize: isMobile ? '11px' : isTablet ? '12px' : '13px',
      fontWeight: '700',
      letterSpacing: '0.3px'
    },
    statValue: {
      fontSize: isMobile ? '26px' : isTablet ? '30px' : '36px',
      fontWeight: '800',
      color: colors.textDark,
      marginBottom: isMobile ? '6px' : '8px',
      position: 'relative',
      zIndex: 1,
      letterSpacing: '-1px',
      wordBreak: 'break-word'
    },
    statLabel: {
      fontSize: isMobile ? '11px' : isTablet ? '12px' : '14px',
      color: colors.textLight,
      fontWeight: '600',
      textTransform: 'uppercase',
      letterSpacing: isMobile ? '0.5px' : '0.8px',
      position: 'relative',
      zIndex: 1
    },
    activitiesSection: {
      background: colors.white,
      padding: isMobile ? '20px' : isTablet ? '26px' : '32px',
      borderRadius: isMobile ? '16px' : isTablet ? '18px' : '20px',
      boxShadow: '0 8px 24px rgba(45, 80, 22, 0.08)',
      border: `1px solid ${colors.mediumGray}`,
      margin: isMobile ? '0 4px' : '0'
    },
    sectionTitle: {
      fontSize: isMobile ? '18px' : isTablet ? '20px' : '24px',
      fontWeight: '800',
      color: colors.textDark,
      marginBottom: isMobile ? '16px' : isTablet ? '20px' : '24px',
      display: 'flex',
      alignItems: 'center',
      gap: isMobile ? '8px' : '12px',
      letterSpacing: '-0.5px'
    },
    activityItem: {
      display: 'flex',
      alignItems: isMobile ? 'flex-start' : 'center',
      gap: isMobile ? '12px' : '16px',
      padding: isMobile ? '14px' : isTablet ? '16px' : '18px',
      borderRadius: isMobile ? '12px' : '14px',
      marginBottom: isMobile ? '10px' : '12px',
      background: colors.lightGray,
      transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
      cursor: 'pointer',
      border: `1px solid transparent`,
      flexDirection: isMobile ? 'column' : 'row'
    },
    activityIconContainer: {
      display: 'flex',
      alignItems: 'center',
      gap: isMobile ? '12px' : '16px',
      width: isMobile ? '100%' : 'auto'
    },
    activityIcon: {
      width: isMobile ? '42px' : isTablet ? '48px' : '52px',
      height: isMobile ? '42px' : isTablet ? '48px' : '52px',
      borderRadius: isMobile ? '10px' : '12px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      fontSize: isMobile ? '20px' : isTablet ? '22px' : '24px',
      boxShadow: '0 4px 12px rgba(45, 80, 22, 0.2)',
      color: colors.white,
      flexShrink: 0
    },
    activityDetails: {
      flex: 1,
      minWidth: 0
    },
    activityAction: {
      fontSize: isMobile ? '13px' : isTablet ? '14px' : '15px',
      fontWeight: '700',
      color: colors.textDark,
      marginBottom: isMobile ? '4px' : '6px',
      letterSpacing: '-0.2px'
    },
    activityInfo: {
      fontSize: isMobile ? '11px' : isTablet ? '12px' : '13px',
      color: colors.textLight,
      fontWeight: '500',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: isMobile ? 'normal' : 'nowrap'
    },
    activityTime: {
      fontSize: isMobile ? '11px' : isTablet ? '12px' : '13px',
      color: colors.textLight,
      fontWeight: '600',
      padding: isMobile ? '5px 10px' : '6px 12px',
      background: colors.white,
      borderRadius: '8px',
      whiteSpace: 'nowrap',
      alignSelf: isMobile ? 'flex-start' : 'center',
      marginLeft: isMobile ? '54px' : '0'
    },
    loadingContainer: {
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      minHeight: isMobile ? '300px' : '400px',
      fontSize: isMobile ? '16px' : '18px',
      color: colors.textLight,
      padding: isMobile ? '20px' : '40px'
    },
    loadingContent: {
      textAlign: 'center'
    },
    loadingIcon: {
      fontSize: isMobile ? '36px' : '48px',
      marginBottom: isMobile ? '12px' : '16px'
    },
    errorContainer: {
      padding: isMobile ? '16px' : '20px',
      background: '#fee2e2',
      border: '1px solid #fca5a5',
      borderRadius: isMobile ? '10px' : '12px',
      color: '#991b1b',
      marginBottom: isMobile ? '16px' : '20px',
      margin: isMobile ? '0 4px 16px 4px' : '0 0 20px 0',
      fontSize: isMobile ? '13px' : '14px'
    },
    refreshButton: {
      padding: isMobile ? '8px 16px' : isTablet ? '9px 18px' : '10px 20px',
      background: colors.primary,
      color: colors.white,
      border: 'none',
      borderRadius: isMobile ? '6px' : '8px',
      cursor: 'pointer',
      fontSize: isMobile ? '12px' : '14px',
      fontWeight: '600',
      transition: 'all 0.3s ease',
      whiteSpace: 'nowrap',
      alignSelf: isMobile ? 'stretch' : 'auto',
      width: isMobile ? '100%' : 'auto'
    },
    emptyState: {
      textAlign: 'center',
      padding: isMobile ? '30px 20px' : '40px',
      color: colors.textLight,
      fontSize: isMobile ? '13px' : '14px'
    },
    emptyIcon: {
      fontSize: isMobile ? '36px' : '48px',
      marginBottom: isMobile ? '12px' : '16px'
    }
  };

  if (loading) {
    return (
      <AdminLayout>
        <div style={styles.loadingContainer}>
          <div style={styles.loadingContent}>
            <div style={styles.loadingIcon}>⏳</div>
            <div>Loading dashboard data...</div>
          </div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      {/* Page Header */}
      <div style={styles.pageHeader}>
        <div style={styles.headerContainer}>
          <div style={{ flex: 1 }}>
            <h1 style={styles.pageTitle}>Dashboard Overview</h1>
            <p style={styles.pageSubtitle}>
              {isMobile
                ? 'Hotel management dashboard'
                : 'Your premium hotel management dashboard with complete control and insights'}
            </p>
          </div>
          <button
            onClick={fetchDashboardData}
            style={styles.refreshButton}
            onMouseEnter={(e) => {
              if (!isMobile) {
                e.target.style.background = colors.accent;
                e.target.style.transform = 'scale(1.05)';
              }
            }}
            onMouseLeave={(e) => {
              if (!isMobile) {
                e.target.style.background = colors.primary;
                e.target.style.transform = 'scale(1)';
              }
            }}
            onTouchStart={(e) => {
              if (isMobile) {
                e.target.style.background = colors.accent;
              }
            }}
            onTouchEnd={(e) => {
              if (isMobile) {
                setTimeout(() => {
                  e.target.style.background = colors.primary;
                }, 150);
              }
            }}
          >
            🔄 Refresh
          </button>
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div style={styles.errorContainer}>
          <strong>⚠️ Error:</strong> {error}
        </div>
      )}

      {/* Statistics Grid */}
      <div style={styles.statsGrid}>
        {statsConfig.map((config, index) => {
          const statData = stats[config.key] || { value: 0, change: '+0%' };
          const displayValue = config.isRevenue
            ? `₹${parseFloat(statData.value || 0).toLocaleString('en-IN')}`
            : statData.value || 0;

          const trend = statData.change.startsWith('+') ? 'up' : 'down';

          return (
            <div
              key={index}
              style={styles.statCard}
              onMouseEnter={(e) => {
                if (!isMobile) {
                  e.currentTarget.style.transform = 'translateY(-8px) scale(1.02)';
                  e.currentTarget.style.boxShadow = '0 16px 40px rgba(45, 80, 22, 0.15)';
                }
              }}
              onMouseLeave={(e) => {
                if (!isMobile) {
                  e.currentTarget.style.transform = 'translateY(0) scale(1)';
                  e.currentTarget.style.boxShadow = '0 4px 16px rgba(45, 80, 22, 0.08)';
                }
              }}
              onTouchStart={(e) => {
                if (isMobile) {
                  e.currentTarget.style.transform = 'scale(0.98)';
                }
              }}
              onTouchEnd={(e) => {
                if (isMobile) {
                  setTimeout(() => {
                    e.currentTarget.style.transform = 'scale(1)';
                  }, 150);
                }
              }}
            >
              <div style={styles.statHeader}>
                <span
                  style={{
                    ...styles.statIcon,
                    background: config.color,
                    color: colors.white
                  }}
                >
                  {config.icon}
                </span>
                <span
                  style={{
                    ...styles.statChange,
                    background: trend === 'up' ? 'rgba(74, 124, 42, 0.15)' : 'rgba(212, 165, 116, 0.15)',
                    color: trend === 'up' ? colors.success : colors.warning
                  }}
                >
                  {statData.change}
                </span>
              </div>
              <div style={styles.statValue}>{displayValue}</div>
              <div style={styles.statLabel}>{config.label}</div>
            </div>
          );
        })}
      </div>

      {/* Recent Activities */}
      <div style={styles.activitiesSection}>
        <h2 style={styles.sectionTitle}>
          <span>📋</span>
          <span>Recent Activities</span>
        </h2>
        {activities.length === 0 ? (
          <div style={styles.emptyState}>
            <div style={styles.emptyIcon}>📭</div>
            <div>No recent activities found</div>
          </div>
        ) : (
          activities.map((activity, index) => (
            <div
              key={index}
              style={styles.activityItem}
              onMouseEnter={(e) => {
                if (!isMobile) {
                  e.currentTarget.style.background = colors.mediumGray;
                  e.currentTarget.style.transform = 'translateX(8px)';
                  e.currentTarget.style.borderColor = colors.primary;
                }
              }}
              onMouseLeave={(e) => {
                if (!isMobile) {
                  e.currentTarget.style.background = colors.lightGray;
                  e.currentTarget.style.transform = 'translateX(0)';
                  e.currentTarget.style.borderColor = 'transparent';
                }
              }}
              onTouchStart={(e) => {
                if (isMobile) {
                  e.currentTarget.style.background = colors.mediumGray;
                }
              }}
              onTouchEnd={(e) => {
                if (isMobile) {
                  setTimeout(() => {
                    e.currentTarget.style.background = colors.lightGray;
                  }, 150);
                }
              }}
            >
              <div style={styles.activityIconContainer}>
                <div style={{
                  ...styles.activityIcon,
                  background: getActivityColor(activity.type)
                }}>
                  {activity.icon}
                </div>
                <div style={styles.activityDetails}>
                  <div style={styles.activityAction}>{activity.action}</div>
                  <div style={styles.activityInfo}>
                    {activity.room} • {activity.guest}
                  </div>
                </div>
              </div>
              <div style={styles.activityTime}>{activity.time}</div>
            </div>
          ))
        )}
      </div>

      {/* Today's Check-ins */}
      <div style={{
        ...styles.activitiesSection,
        marginTop: isMobile ? '24px' : isTablet ? '32px' : '40px'
      }}>
        <h2 style={styles.sectionTitle}>
          <span>🔑</span>
          <span>Today's Check-ins</span>
        </h2>
        {todayCheckIns.length === 0 ? (
          <div style={styles.emptyState}>
            <div style={styles.emptyIcon}>📭</div>
            <div>No check-ins scheduled for today</div>
          </div>
        ) : (
          <div style={{
            display: 'grid',
            gridTemplateColumns: isMobile ? '1fr' : isTablet ? 'repeat(2, 1fr)' : 'repeat(3, 1fr)',
            gap: isMobile ? '16px' : '20px'
          }}>
            {todayCheckIns.map((guest, index) => (
              <div
                key={index}
                style={{
                  background: colors.lightGray,
                  padding: isMobile ? '16px' : '20px',
                  borderRadius: isMobile ? '12px' : '14px',
                  border: `2px solid ${colors.mediumGray}`,
                  transition: 'all 0.3s ease',
                  cursor: 'pointer'
                }}
                onMouseEnter={(e) => {
                  if (!isMobile) {
                    e.currentTarget.style.borderColor = colors.primary;
                    e.currentTarget.style.transform = 'translateY(-4px)';
                    e.currentTarget.style.boxShadow = '0 8px 20px rgba(45, 80, 22, 0.15)';
                  }
                }}
                onMouseLeave={(e) => {
                  if (!isMobile) {
                    e.currentTarget.style.borderColor = colors.mediumGray;
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.boxShadow = 'none';
                  }
                }}
              >
                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  marginBottom: '12px'
                }}>
                  <div style={{
                    background: colors.primary,
                    color: colors.white,
                    padding: isMobile ? '6px 12px' : '8px 14px',
                    borderRadius: '8px',
                    fontSize: isMobile ? '13px' : '14px',
                    fontWeight: '700'
                  }}>
                    Room {guest.room_number}
                  </div>
                  <div style={{
                    background: guest.payment_status === 'completed' ? 'rgba(16, 185, 129, 0.15)' :
                      guest.payment_status === 'partial' ? 'rgba(245, 158, 11, 0.15)' :
                        'rgba(239, 68, 68, 0.15)',
                    color: guest.payment_status === 'completed' ? colors.success :
                      guest.payment_status === 'partial' ? colors.warning :
                        colors.danger,
                    padding: isMobile ? '4px 10px' : '6px 12px',
                    borderRadius: '6px',
                    fontSize: isMobile ? '11px' : '12px',
                    fontWeight: '600',
                    textTransform: 'capitalize'
                  }}>
                    {guest.payment_status}
                  </div>
                </div>
                <div style={{
                  fontSize: isMobile ? '15px' : '16px',
                  fontWeight: '700',
                  color: colors.textDark,
                  marginBottom: '8px'
                }}>
                  {guest.guest_name}
                </div>
                <div style={{
                  fontSize: isMobile ? '12px' : '13px',
                  color: colors.textLight,
                  marginBottom: '4px'
                }}>
                  📧 {guest.guest_email || 'N/A'}
                </div>
                <div style={{
                  fontSize: isMobile ? '12px' : '13px',
                  color: colors.textLight,
                  marginBottom: '4px'
                }}>
                  📞 {guest.guest_phone || 'N/A'}
                </div>
                <div style={{
                  fontSize: isMobile ? '12px' : '13px',
                  color: colors.textLight,
                  marginBottom: '8px'
                }}>
                  👥 {guest.number_of_guests} Guest{guest.number_of_guests > 1 ? 's' : ''}
                </div>
                <div style={{
                  borderTop: `1px solid ${colors.mediumGray}`,
                  paddingTop: '8px',
                  marginTop: '8px',
                  display: 'flex',
                  justifyContent: 'space-between',
                  fontSize: isMobile ? '12px' : '13px'
                }}>
                  <span style={{ color: colors.textLight }}>Check-out:</span>
                  <span style={{ fontWeight: '600', color: colors.textDark }}>
                    {new Date(guest.check_out_date).toLocaleDateString('en-IN')}
                  </span>
                </div>
                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  fontSize: isMobile ? '12px' : '13px',
                  marginTop: '4px'
                }}>
                  <span style={{ color: colors.textLight }}>Total Amount:</span>
                  <span style={{ fontWeight: '700', color: colors.primary }}>
                    ₹{parseFloat(guest.total_amount).toLocaleString('en-IN')}
                  </span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Today's Check-outs */}
      <div style={{
        ...styles.activitiesSection,
        marginTop: isMobile ? '24px' : isTablet ? '32px' : '40px'
      }}>
        <h2 style={styles.sectionTitle}>
          <span>🚶</span>
          <span>Today's Check-outs</span>
        </h2>
        {todayCheckOuts.length === 0 ? (
          <div style={styles.emptyState}>
            <div style={styles.emptyIcon}>📭</div>
            <div>No check-outs scheduled for today</div>
          </div>
        ) : (
          <div style={{
            display: 'grid',
            gridTemplateColumns: isMobile ? '1fr' : isTablet ? 'repeat(2, 1fr)' : 'repeat(3, 1fr)',
            gap: isMobile ? '16px' : '20px'
          }}>
            {todayCheckOuts.map((guest, index) => (
              <div
                key={index}
                style={{
                  background: colors.lightGray,
                  padding: isMobile ? '16px' : '20px',
                  borderRadius: isMobile ? '12px' : '14px',
                  border: `2px solid ${colors.mediumGray}`,
                  transition: 'all 0.3s ease',
                  cursor: 'pointer'
                }}
                onMouseEnter={(e) => {
                  if (!isMobile) {
                    e.currentTarget.style.borderColor = colors.primary;
                    e.currentTarget.style.transform = 'translateY(-4px)';
                    e.currentTarget.style.boxShadow = '0 8px 20px rgba(45, 80, 22, 0.15)';
                  }
                }}
                onMouseLeave={(e) => {
                  if (!isMobile) {
                    e.currentTarget.style.borderColor = colors.mediumGray;
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.boxShadow = 'none';
                  }
                }}
              >
                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  marginBottom: '12px'
                }}>
                  <div style={{
                    background: colors.warning,
                    color: colors.white,
                    padding: isMobile ? '6px 12px' : '8px 14px',
                    borderRadius: '8px',
                    fontSize: isMobile ? '13px' : '14px',
                    fontWeight: '700'
                  }}>
                    Room {guest.room_number}
                  </div>
                  <div style={{
                    background: guest.status === 'checked_out'
                      ? 'rgba(107, 168, 62, 0.15)'
                      : guest.pending_amount > 0
                        ? 'rgba(239, 68, 68, 0.15)'
                        : 'rgba(16, 185, 129, 0.15)',
                    color: guest.status === 'checked_out'
                      ? colors.accent
                      : guest.pending_amount > 0
                        ? colors.danger
                        : colors.success,
                    padding: isMobile ? '4px 10px' : '6px 12px',
                    borderRadius: '6px',
                    fontSize: isMobile ? '11px' : '12px',
                    fontWeight: '600'
                  }}>
                    {guest.status === 'checked_out'
                      ? 'Already Checked Out'
                      : guest.pending_amount > 0
                        ? 'Pending Payment'
                        : 'Ready to Checkout'}
                  </div>
                </div>
                <div style={{
                  fontSize: isMobile ? '15px' : '16px',
                  fontWeight: '700',
                  color: colors.textDark,
                  marginBottom: '8px'
                }}>
                  {guest.guest_name}
                </div>
                <div style={{
                  fontSize: isMobile ? '12px' : '13px',
                  color: colors.textLight,
                  marginBottom: '4px'
                }}>
                  📧 {guest.guest_email || 'N/A'}
                </div>
                <div style={{
                  fontSize: isMobile ? '12px' : '13px',
                  color: colors.textLight,
                  marginBottom: '4px'
                }}>
                  📞 {guest.guest_phone || 'N/A'}
                </div>
                <div style={{
                  fontSize: isMobile ? '12px' : '13px',
                  color: colors.textLight,
                  marginBottom: '8px'
                }}>
                  👥 {guest.number_of_guests} Guest{guest.number_of_guests > 1 ? 's' : ''}
                </div>
                <div style={{
                  borderTop: `1px solid ${colors.mediumGray}`,
                  paddingTop: '8px',
                  marginTop: '8px',
                  display: 'flex',
                  justifyContent: 'space-between',
                  fontSize: isMobile ? '12px' : '13px'
                }}>
                  <span style={{ color: colors.textLight }}>Total Amount:</span>
                  <span style={{ fontWeight: '700', color: colors.textDark }}>
                    ₹{parseFloat(guest.total_amount).toLocaleString('en-IN')}
                  </span>
                </div>
                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  fontSize: isMobile ? '12px' : '13px',
                  marginTop: '4px'
                }}>
                  <span style={{ color: colors.textLight }}>Paid:</span>
                  <span style={{ fontWeight: '600', color: colors.success }}>
                    ₹{parseFloat(guest.total_paid).toLocaleString('en-IN')}
                  </span>
                </div>
                {guest.pending_amount > 0 && (
                  <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    fontSize: isMobile ? '12px' : '13px',
                    marginTop: '4px'
                  }}>
                    <span style={{ color: colors.textLight }}>Pending:</span>
                    <span style={{ fontWeight: '700', color: colors.danger }}>
                      ₹{parseFloat(guest.pending_amount).toLocaleString('en-IN')}
                    </span>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </AdminLayout>
  );
};

export default AdminDashboard;